// Polyfill Map.prototype.getOrInsertComputed for Safari/older browsers.
// pdfjs-dist v5.5+ uses this internally; it's only Baseline since Feb 2026.
/* eslint-disable @typescript-eslint/no-explicit-any */
if (!(Map.prototype as any).getOrInsertComputed) {
  (Map.prototype as any).getOrInsertComputed = function (key: any, cb: (k: any) => any) {
    if (this.has(key)) return this.get(key);
    const v = cb(key);
    this.set(key, v);
    return v;
  };
}
/* eslint-enable @typescript-eslint/no-explicit-any */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';
import { getProxiedStorageUrl } from '@/lib/storageProxy';
import { ViewerShell } from './ViewerShell';
import type { FavoriteCachedData } from '@/types/favorites';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

interface PDFViewerProps {
  url: string;
  title?: string;
  onClose: () => void;
  onDownload?: () => void;
  onPageChange?: (currentPage: number, totalPages: number) => void;
  fileId?: string;
  favoriteCachedData?: FavoriteCachedData;
}

export function PDFViewer({
  url,
  title,
  onClose,
  onDownload,
  onPageChange,
  fileId,
  favoriteCachedData,
}: PDFViewerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [pdf, setPdf] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.0);
  const [rotation, setRotation] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);

  useEffect(() => {
    const loadPdf = async () => {
      setLoading(true);
      setError(null);
      try {
        const loadingTask = pdfjsLib.getDocument(getProxiedStorageUrl(url));
        const pdfDoc = await loadingTask.promise;
        setPdf(pdfDoc);
        setTotalPages(pdfDoc.numPages);

        // Calculate fit-to-width initial scale from the first page
        const firstPage = await pdfDoc.getPage(1);
        const nativeViewport = firstPage.getViewport({ scale: 1 });
        const container = containerRef.current;
        if (container) {
          const style = getComputedStyle(container);
          const contentWidth =
            container.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
          const fitScale = (contentWidth / nativeViewport.width) * 0.98;
          const clampedFit = Math.max(0.5, Math.min(fitScale, 1));
          setScale(Math.round(clampedFit * 100) / 100);
        }
        setLoading(false);
      } catch (err) {
        console.error('Failed to load PDF:', err);
        setError('Failed to load PDF document');
        setLoading(false);
      }
    };
    loadPdf();
    return () => {
      pdf?.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  useEffect(() => {
    if (!pdf || loading || !canvasRef.current) return;

    let cancelled = false;
    let renderTask: { promise: Promise<void>; cancel(): void } | null = null;
    const canvas = canvasRef.current;

    setRendering(true);

    (async () => {
      try {
        const page = await pdf.getPage(currentPage);
        if (cancelled) return;

        const context = canvas.getContext('2d');
        if (!context) return;

        const dpr = window.devicePixelRatio || 1;
        const viewport = page.getViewport({ scale: scale * dpr, rotation });

        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.width = `${Math.floor(viewport.width / dpr)}px`;
        canvas.style.height = `${Math.floor(viewport.height / dpr)}px`;

        renderTask = page.render({ canvas: null, canvasContext: context, viewport });
        await renderTask.promise;
      } catch (err) {
        if (!cancelled) console.error('Failed to render page:', err);
      } finally {
        if (!cancelled) setRendering(false);
      }
    })();

    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [pdf, currentPage, scale, rotation, loading]);

  useEffect(() => {
    if (totalPages > 0 && onPageChange) onPageChange(currentPage, totalPages);
  }, [currentPage, totalPages, onPageChange]);

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    },
    [totalPages]
  );

  const handleScaleChange = useCallback((s: number) => {
    setScale(Math.max(0.5, Math.min(3, s)));
  }, []);

  const handleRotate = useCallback(() => {
    setRotation((r) => (r + 90) % 360);
  }, []);

  return (
    <ViewerShell
      title={title || 'PDF Document'}
      type="pdf"
      fileId={fileId}
      favoriteCachedData={favoriteCachedData}
      onClose={onClose}
      onDownload={onDownload}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={handlePageChange}
      scale={scale}
      onScaleChange={handleScaleChange}
      onRotate={handleRotate}
      swipeEnabled={scale <= 1}
      keyboardHint="Arrow keys to navigate • +/- to zoom • R to rotate • Esc to close"
    >
      <div ref={containerRef} className="min-h-full p-2 sm:p-4 flex flex-col">
        {loading && (
          <div className="text-[#8e4585] flex items-center gap-3 m-auto">
            <Loader className="animate-spin" size={24} />
            <span className="text-[#5a3d58] text-sm">Loading PDF...</span>
          </div>
        )}

        {error && (
          <div className="text-center px-4 m-auto">
            <p className="text-red-500 text-base font-medium">{error}</p>
            {onDownload && (
              <button
                onClick={onDownload}
                className="mt-4 px-4 py-2 bg-[#8e4585] text-white rounded-lg hover:bg-[#753a6f] transition-colors text-sm"
              >
                Download PDF instead
              </button>
            )}
          </div>
        )}

        <div
          className="relative inline-block m-auto"
          style={loading || error ? { visibility: 'hidden', position: 'absolute' } : undefined}
        >
          <canvas ref={canvasRef} className="shadow-lg bg-white rounded" />
          {rendering && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/30">
              <Loader className="animate-spin text-[#8e4585]" size={28} />
            </div>
          )}
        </div>
      </div>
    </ViewerShell>
  );
}
